import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { FileItem } from '@/types'
import apiService from '@/services/api'

interface FilesState {
  byFolderId: Record<number | string, FileItem[]> // Including 'root' for root files
  byId: Record<number, FileItem>
  isLoading: Record<number | string, boolean> // Track loading state per folder
  error: string | null
}

const initialState: FilesState = {
  byFolderId: {},
  byId: {},
  isLoading: {},
  error: null,
}

// Helper functions
const buildFileMap = (files: FileItem[]): Record<number, FileItem> => {
  const map: Record<number, FileItem> = {}
  files.forEach((file) => {
    map[file.id] = file
  })
  return map
}

// Async thunks
export const fetchFolderFiles = createAsyncThunk(
  'files/fetchFolderFiles',
  async (folderId: number | null, { rejectWithValue }) => {
    try {
      const files =
        folderId === null
          ? await apiService.getRootFiles()
          : await apiService.getFolderFiles(folderId)

      return {
        files,
        folderId: folderId === null ? 'root' : folderId,
      }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || error.message || 'Failed to fetch files'
      )
    }
  }
)

export const moveFile = createAsyncThunk(
  'files/move',
  async (
    {
      fileId,
      targetFolderId,
      sourceFolderId,
    }: {
      fileId: number
      targetFolderId?: number
      sourceFolderId?: number | null
    },
    { rejectWithValue }
  ) => {
    try {
      await apiService.moveFile(fileId, targetFolderId)
      return {
        fileId,
        targetFolderId: targetFolderId || 'root',
        sourceFolderId: sourceFolderId === null ? 'root' : sourceFolderId,
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to move file')
    }
  }
)

export const moveMultipleFiles = createAsyncThunk(
  'files/moveMultiple',
  async (
    {
      fileIds,
      targetFolderId,
      sourceFolderId,
    }: {
      fileIds: number[]
      targetFolderId?: number
      sourceFolderId?: number | null
    },
    { rejectWithValue }
  ) => {
    try {
      // Move all files
      const movePromises = fileIds.map((fileId) => apiService.moveFile(fileId, targetFolderId))
      await Promise.all(movePromises)

      return {
        fileIds,
        targetFolderId: targetFolderId || 'root',
        sourceFolderId: sourceFolderId === null ? 'root' : sourceFolderId,
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to move files')
    }
  }
)

export const renameFile = createAsyncThunk(
  'files/rename',
  async ({ fileId, displayName }: { fileId: number; displayName: string }, { rejectWithValue }) => {
    try {
      const updatedFile = await apiService.renameFile(fileId, displayName)
      return updatedFile
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || error.message || 'Failed to rename file'
      )
    }
  }
)

export const deleteFile = createAsyncThunk(
  'files/delete',
  async (
    { fileId, folderId }: { fileId: number; folderId?: number | null },
    { rejectWithValue }
  ) => {
    try {
      await apiService.deleteFile(fileId)
      return {
        fileId,
        folderId: folderId === null || folderId === undefined ? 'root' : folderId,
      }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || error.message || 'Failed to delete file'
      )
    }
  }
)

const filesSlice = createSlice({
  name: 'files',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },

    // Optimistic updates
    optimisticMoveFile: (
      state,
      action: PayloadAction<{
        fileId: number
        targetFolderId: number | string
        sourceFolderId: number | string
      }>
    ) => {
      const { fileId, targetFolderId, sourceFolderId } = action.payload
      const file = state.byId[fileId]

      if (file) {
        // Remove from source folder
        if (state.byFolderId[sourceFolderId]) {
          state.byFolderId[sourceFolderId] = state.byFolderId[sourceFolderId].filter(
            (f) => f.id !== fileId
          )
        }

        // Add to target folder
        if (!state.byFolderId[targetFolderId]) {
          state.byFolderId[targetFolderId] = []
        }

        // Update file's folder_id
        const updatedFile = {
          ...file,
          folder_id: targetFolderId === 'root' ? null : (targetFolderId as number),
        }

        state.byFolderId[targetFolderId].push(updatedFile)
        state.byId[fileId] = updatedFile
      }
    },

    optimisticMoveMultipleFiles: (
      state,
      action: PayloadAction<{
        fileIds: number[]
        targetFolderId: number | string
        sourceFolderId: number | string
      }>
    ) => {
      const { fileIds, targetFolderId, sourceFolderId } = action.payload

      fileIds.forEach((fileId) => {
        const file = state.byId[fileId]

        if (file) {
          // Remove from source folder
          if (state.byFolderId[sourceFolderId]) {
            state.byFolderId[sourceFolderId] = state.byFolderId[sourceFolderId].filter(
              (f) => f.id !== fileId
            )
          }

          // Add to target folder
          if (!state.byFolderId[targetFolderId]) {
            state.byFolderId[targetFolderId] = []
          }

          // Update file's folder_id
          const updatedFile = {
            ...file,
            folder_id: targetFolderId === 'root' ? null : (targetFolderId as number),
          }

          state.byFolderId[targetFolderId].push(updatedFile)
          state.byId[fileId] = updatedFile
        }
      })
    },

    // Add new file to store (for uploads)
    addFile: (state, action: PayloadAction<{ file: FileItem; folderId?: number | null }>) => {
      const { file, folderId } = action.payload
      const folderKey = folderId === null || folderId === undefined ? 'root' : folderId

      state.byId[file.id] = file

      if (!state.byFolderId[folderKey]) {
        state.byFolderId[folderKey] = []
      }

      // Idempotent by id: the same upload can be reported from more than one
      // path, and a second insert would render a duplicate row.
      if (state.byFolderId[folderKey].some((existing) => existing.id === file.id)) {
        return
      }

      // Add to beginning of array for better UX
      state.byFolderId[folderKey].unshift(file)
    },

    // Remove file from store (for deletes)
    removeFile: (state, action: PayloadAction<{ fileId: number; folderId?: number | null }>) => {
      const { fileId, folderId } = action.payload
      const folderKey = folderId === null || folderId === undefined ? 'root' : folderId

      delete state.byId[fileId]

      if (state.byFolderId[folderKey]) {
        state.byFolderId[folderKey] = state.byFolderId[folderKey].filter((f) => f.id !== fileId)
      }
    },

    // Update file in store
    updateFile: (state, action: PayloadAction<FileItem>) => {
      const file = action.payload
      state.byId[file.id] = file

      // Update in folder arrays
      Object.keys(state.byFolderId).forEach((folderId) => {
        const index = state.byFolderId[folderId].findIndex((f) => f.id === file.id)
        if (index !== -1) {
          state.byFolderId[folderId][index] = file
        }
      })
    },
  },

  extraReducers: (builder) => {
    builder
      // Fetch folder files
      .addCase(fetchFolderFiles.pending, (state, action) => {
        const folderId = action.meta.arg === null ? 'root' : action.meta.arg
        state.isLoading[folderId] = true
        state.error = null
      })
      .addCase(fetchFolderFiles.fulfilled, (state, action) => {
        const { files, folderId } = action.payload
        state.isLoading[folderId] = false
        state.byFolderId[folderId] = files

        // Update byId map
        const fileMap = buildFileMap(files)
        state.byId = { ...state.byId, ...fileMap }
      })
      .addCase(fetchFolderFiles.rejected, (state, action) => {
        const folderId = action.meta.arg === null ? 'root' : action.meta.arg
        state.isLoading[folderId] = false
        state.error = action.payload as string
      })

      // Move file
      .addCase(moveFile.pending, (state) => {
        state.error = null
      })
      .addCase(moveFile.fulfilled, (state, action) => {
        // Optimistic update was already applied, no need to do anything
      })
      .addCase(moveFile.rejected, (state, action) => {
        state.error = action.payload as string
        // TODO: Revert optimistic move on error
      })

      // Move multiple files
      .addCase(moveMultipleFiles.pending, (state) => {
        state.error = null
      })
      .addCase(moveMultipleFiles.fulfilled, (state, action) => {
        // Optimistic update was already applied, no need to do anything
      })
      .addCase(moveMultipleFiles.rejected, (state, action) => {
        state.error = action.payload as string
        // TODO: Revert optimistic move on error
      })

      // Rename file
      .addCase(renameFile.fulfilled, (state, action) => {
        const updatedFile = action.payload
        state.byId[updatedFile.id] = updatedFile

        // Update in folder arrays
        Object.keys(state.byFolderId).forEach((folderId) => {
          const index = state.byFolderId[folderId].findIndex((f) => f.id === updatedFile.id)
          if (index !== -1) {
            state.byFolderId[folderId][index] = updatedFile
          }
        })
      })
      .addCase(renameFile.rejected, (state, action) => {
        state.error = action.payload as string
      })

      // Delete file
      .addCase(deleteFile.fulfilled, (state, action) => {
        const { fileId, folderId } = action.payload

        // Remove from byId
        delete state.byId[fileId]

        // Remove from folder array
        if (state.byFolderId[folderId]) {
          state.byFolderId[folderId] = state.byFolderId[folderId].filter((f) => f.id !== fileId)
        }
      })
      .addCase(deleteFile.rejected, (state, action) => {
        state.error = action.payload as string
      })
  },
})

export const {
  clearError,
  optimisticMoveFile,
  optimisticMoveMultipleFiles,
  addFile,
  removeFile,
  updateFile,
} = filesSlice.actions

export default filesSlice.reducer
